import {
  Button,
  Divider,
  Notice,
  NumberField,
  Select,
  Stack,
  Text,
  Truncate,
  ValueFormatter,
} from '@eleveo/components-react';
import { WarningIcon } from '@eleveo/icons-react';
import { TFunction } from 'i18next';
import React from 'react';
import { Form, FormProps, FormRenderProps, useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import {
  CANCEL_NOTIFICATION_BUTTON_TAG,
  NOTICE_DEVIATION_TAG,
  NOTICE_SLA_TAG,
  SAVE_NOTIFICATION_BUTTON_TAG,
  getFieldsInputTag,
} from './constants';
import { IntradayQueue, IntradayQueueNotification } from '../../graphql/components';
import { required, requiredWithOtherFields } from '../../utils/validations';
import { composeValidators } from '../../utils/validations/composeValidators';
import { inRange } from '../../utils/validations/inRange';
import { requiredAtLeastOneOfFields } from '../../utils/validations/requiredAtLeastOneOfFields';

export const intradayQueuesToOptions = (queuesList: IntradayQueue[], t: TFunction) =>
  queuesList.map(({ id, name, inAlert }: IntradayQueue) => ({
    id,
    inAlert,
    name: inAlert ? (
      <>
        <WarningIcon aria-label={t('layout.inAlert')} className="text-grey-600 mie-1" />
        {name}
      </>
    ) : (
      name
    ),
    textValue: name,
  }));

export const intradayQueuesValueFormatter: (t: TFunction) => ValueFormatter<IntradayQueuesToOptions[0]> =
  (t: TFunction) => (items) => {
    const [
      {
        value: { inAlert, textValue },
      },
    ] = items;

    return (
      <div className="flex items-center">
        {inAlert && <WarningIcon aria-label={t('layout.inAlert')} className="text-grey-600 mie-1" />}
        <Truncate>{textValue}</Truncate>
      </div>
    );
  };

export type NotificationFormType = {
  contactVolumeDeviation: number;
  queueId: string;
  slaOutOfRangeFrom: number;
  slaOutOfRangeTo: number;
};

export type IntradayNotificationToDuplicate = Omit<NotificationFormType, 'queueId'>;

export type IntradayQueuesToOptions = ReturnType<typeof intradayQueuesToOptions>;

interface NotificationFormProps {
  handleCancel: () => void;
  intradayQueueNotification?: IntradayQueueNotification | IntradayNotificationToDuplicate;
  isCreate?: boolean;
  onSubmit: FormProps<NotificationFormType>['onSubmit'];
  queuesList: IntradayQueuesToOptions;
}

interface NotificationFormInnerProps {
  handleCancel: () => void;
  isCreate?: boolean;
  queuesList: IntradayQueuesToOptions;
}

const initialConstraintsConfig: Record<keyof IntradayNotificationToDuplicate, { max: number; min: number }> = {
  contactVolumeDeviation: {
    min: 0,
    max: 10,
  },
  slaOutOfRangeFrom: {
    min: 0,
    max: 1,
  },
  slaOutOfRangeTo: {
    min: 0,
    max: 1,
  },
};

// NaN value needed to set into components fields instead of null value to ensure empty initialization of inputs
const normalizeNotificationToFormValues = (
  notification?: IntradayQueueNotification | IntradayNotificationToDuplicate
) => ({
  ...(notification ?? {}), // queueId value
  contactVolumeDeviation: notification?.contactVolumeDeviation ?? NaN,
  slaOutOfRangeFrom: notification?.slaOutOfRangeFrom ?? NaN,
  slaOutOfRangeTo: notification?.slaOutOfRangeTo ?? NaN,
});

const NotificationFormInner: React.FC<FormRenderProps<NotificationFormType> & NotificationFormInnerProps> = ({
  handleCancel,
  handleSubmit,
  invalid,
  isCreate = false,
  pristine,
  queuesList,
}) => {
  const { t } = useTranslation();

  const queueField = useField('queueId', {
    validate: required,
  });

  const deviationField = useField<number>('contactVolumeDeviation', {
    validate: composeValidators(
      requiredAtLeastOneOfFields([['slaOutOfRangeFrom'], ['slaOutOfRangeTo']]),
      inRange(initialConstraintsConfig.contactVolumeDeviation.min, initialConstraintsConfig.contactVolumeDeviation.max)
    ),
  });

  const slaFromField = useField<number>('slaOutOfRangeFrom', {
    validate: (value, values) =>
      composeValidators(
        inRange(
          initialConstraintsConfig.slaOutOfRangeFrom.min,
          (values as NotificationFormType).slaOutOfRangeTo ?? initialConstraintsConfig.slaOutOfRangeFrom.max
        ),
        requiredAtLeastOneOfFields([['contactVolumeDeviation']]),
        requiredWithOtherFields([['slaOutOfRangeTo']])
      )(value, values),
  });

  const slaToField = useField<number>('slaOutOfRangeTo', {
    validate: (value, values) =>
      composeValidators(
        inRange(
          (values as NotificationFormType).slaOutOfRangeFrom ?? initialConstraintsConfig.slaOutOfRangeTo.min,
          initialConstraintsConfig.slaOutOfRangeTo.max
        ),
        requiredAtLeastOneOfFields([['contactVolumeDeviation']]),
        requiredWithOtherFields([['slaOutOfRangeFrom']])
      )(value, values),
  });

  return (
    <form>
      <Stack direction="vertical" className="w-full p-20" spacing={20}>
        <Select
          className="w-full"
          isDisabled={!isCreate}
          label={t('queue.queue')}
          onSelectionChange={(newSelection) => queueField.input.onChange(Array.from(newSelection))}
          options={queuesList}
          selectedKeys={queueField.input.value}
          selectionMode="single"
          testId={getFieldsInputTag('queueId')}
        />

        <Text weight="bold">{t('intraday.charts.contactVolumes')}</Text>
        <div className="w-full max-w-full flex items-center gap-40">
          <Text>{t('intraday.deviation')}:</Text>
          <NumberField
            className="w-auto flex-1"
            {...deviationField.input}
            formatOptions={{
              style: 'percent',
            }}
            label={t('intraday.value')}
            maxValue={initialConstraintsConfig.contactVolumeDeviation.max}
            minValue={initialConstraintsConfig.contactVolumeDeviation.min}
            testId={getFieldsInputTag('contactVolumeDeviation')}
            validationState={deviationField.meta.touched && deviationField.meta.invalid ? 'invalid' : undefined}
          />
        </div>
        <Notice testId={NOTICE_DEVIATION_TAG}>{t('intraday.averageDifferenceInPastHour')}</Notice>

        <Divider />

        <Text weight="bold">{t('intraday.charts.serviceLevelAgreement')}</Text>
        <div className="w-full flex items-center gap-20">
          <Text className="whitespace-nowrap">{t('intraday.outOfRange')}:</Text>
          <NumberField
            className="w-auto"
            {...slaFromField.input}
            formatOptions={{
              style: 'percent',
            }}
            label={t('intraday.from')}
            maxValue={initialConstraintsConfig.slaOutOfRangeFrom.max}
            minValue={initialConstraintsConfig.slaOutOfRangeFrom.min}
            testId={getFieldsInputTag('slaOutOfRangeFrom')}
            validationState={slaFromField.meta.touched && slaFromField.meta.invalid ? 'invalid' : undefined}
          />
          <NumberField
            className="w-auto"
            {...slaToField.input}
            formatOptions={{
              style: 'percent',
            }}
            label={t('intraday.to')}
            maxValue={initialConstraintsConfig.slaOutOfRangeTo.max}
            minValue={initialConstraintsConfig.slaOutOfRangeTo.min}
            testId={getFieldsInputTag('slaOutOfRangeTo')}
            validationState={slaToField.meta.touched && slaToField.meta.invalid ? 'invalid' : undefined}
          />
        </div>
        <Notice testId={NOTICE_SLA_TAG}>{t('intraday.averageSla')}</Notice>

        <Divider />

        <div className="w-full flex justify-end gap-15">
          <Button testId={CANCEL_NOTIFICATION_BUTTON_TAG} variant="outline" onClick={handleCancel}>
            {t('form.cancel')}
          </Button>
          <Button
            color="success"
            isDisabled={pristine || invalid}
            onClick={handleSubmit}
            testId={SAVE_NOTIFICATION_BUTTON_TAG}
          >
            {t('layout.action.save')}
          </Button>
        </div>
      </Stack>
    </form>
  );
};

export const NotificationForm: React.FC<NotificationFormProps> = ({
  handleCancel,
  intradayQueueNotification,
  isCreate,
  onSubmit,
  queuesList,
}) => (
  <Form<NotificationFormType>
    keepDirtyOnReinitialize
    onSubmit={onSubmit}
    initialValues={normalizeNotificationToFormValues(intradayQueueNotification)}
  >
    {(renderProps) => (
      <NotificationFormInner {...renderProps} handleCancel={handleCancel} isCreate={isCreate} queuesList={queuesList} />
    )}
  </Form>
);
